import { useApolloClient } from "@apollo/client";
import { MenuItem, Text } from "@chakra-ui/react";
import React from "react";
import { Glossary } from "src/components/Text/Glossary";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useSubStatusTransition } from "src/hooks/useSubStatusTransition";
import { maybePluralize } from "src/services/format";
import * as GQL from "src/types/graphql";
import { WaitlistError } from "../errors";
import {
  FETCH_OFFER_WAITLIST_STATUS,
  GET_ASSIGNED_FORMS_BY_FORM_ID,
} from "../graphql/queries";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canAddToWaitlists } from "../validations/addToWaitlists";
import { trackFormAction, trackFormActionError } from "./trackFormAction";

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
  onRefetch?: () => void;
  menuLabel?: string;
  isBulkAction?: boolean;
}

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "You will add the selected form to its school and grade waitlist. You can remove it anytime.",
    "You will add the selected forms to each school and grade waitlist. You can remove them anytime."
  );

const confirmButtonLabel = (count: number) =>
  maybePluralize(count, "Yes, add to waitlist", "Yes, add to waitlists");

export const AddToWaitlists = ({
  selectedFormSchoolStatuses,
  selectedFormSchools,
  onRefetch,
  menuLabel = "Add to waitlists",
  isBulkAction = false,
}: Props) => {
  const { mutateAsync: addToWaitListBulk } = FSQ.usePostWaitlists();
  const { isSubStatusTransitionAllowed } = useSubStatusTransition();

  const toast = useAvelaToast();
  const client = useApolloClient();

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    translate: true,
  });

  const handleOnClick = async (evt: React.MouseEvent) => {
    const schoolForms = selectedFormSchoolStatuses;
    const count = schoolForms.length;
    const trackArgs = {
      actionType: "ADD_TO_WAITLIST" as const,
      bulk: isBulkAction,
      count,
      async: false,
    };

    const formNoun = maybePluralize(count, "form");

    trackFormAction({ verb: "Clicked", ...trackArgs });

    if (
      await confirm({
        header: (
          <Text>{`Add ${count} ${formNoun} to ${maybePluralize(
            count,
            "waitlist"
          )}?`}</Text>
        ),
        body: (
          <Text>
            <Glossary>{confirmationBodyText(count)}</Glossary>
          </Text>
        ),
        confirmButton: {
          label: confirmButtonLabel(count),
          colorScheme: "blue",
        },
      })
    ) {
      trackFormAction({ verb: "Confirmed", ...trackArgs });

      const loadingToastId = toast({
        title: `Working hard`,
        description: `Waitlisting ${count} ${formNoun}...`,
        status: "loading",
        duration: null,
      });

      try {
        await addToWaitListBulk({
          body: {
            data: {
              waitlists: schoolForms.map((schoolForm) => ({
                form_id: schoolForm.form_id,
                school_id: schoolForm.school_id,
              })),
            },
          },
        });
        await client.refetchQueries({
          include: [FETCH_OFFER_WAITLIST_STATUS, GET_ASSIGNED_FORMS_BY_FORM_ID],
        });
        if (onRefetch) onRefetch();

        toast.close(loadingToastId);
        toast({
          title: "Hooray",
          description: `${count} ${formNoun} waitlisted`,
          status: "success",
        });
        trackFormAction({ verb: "Completed", ...trackArgs });
      } catch (err: any) {
        trackFormActionError(err, trackArgs);
        toast.close(loadingToastId);
        let message = "Check your network and try again.";

        if (err instanceof WaitlistError) {
          message = err.message;
        }

        toast.error({
          title: "Oops! Something went wrong!",
          description: message,
        });
        console.error(err);
      }
    } else {
      trackFormAction({ verb: "Canceled", ...trackArgs });
    }
  };

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canAddToWaitlists(
            selectedFormSchoolStatuses,
            selectedFormSchools,
            (subStatus) =>
              isSubStatusTransitionAllowed(
                subStatus,
                GQL.waitlist_status_enum.Waitlisted
              )
          )
        }
      >
        {menuLabel}
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
